import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import CompanyHeader from '@/components/company/CompanyHeader';
import { SecondaryButton, SubmitButton } from '@/components/ui/Button';
import useTenantState from '@/states/tenant.state';
import { getKpiApprovalDetail, updateKpiApproval, updateKpiApprovalItems } from '@/api/myKpi';
import { errorHandler } from '@/utils/functions/errorHandler';
import {
  NameMap,
  StatusBadge,
  InfoRow,
  formatDate,
  resolveNameMap,
  ApprovalStatusBanner,
  ApprovedKpiView,
  KpiItemsTable,
  IApprovedKpiItem,
} from '@/pages/company-profile/kpi/kpiShared';
import { toast } from 'react-toastify';

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface IApprovalKpiItem extends IApprovedKpiItem {
  manager_actual: number | null;
  aob: number | null;
  score: number | null;
  measurement?: string;
  evidence_url: string;
  org_key_activity?: { id: number; name: string };
  org_job_description?: { id: number; name: string };
}

interface IApprovalKpiDetail {
  id: number;
  tenant_id: number;
  kpi_template_id: number;
  user_id: number;
  period: string;
  start_date?: string;
  end_date?: string;
  status: string;
  progress: number | null;
  score: number | null;
  category: string | null;
  notes_employee: string;
  notes_manager: string;
  created_at: string;
  updated_at: string;
  items: IApprovalKpiItem[];
  template?: { id: number; name: string; period?: string; start_date?: string; end_date?: string };
  user?: { id: number; name: string; email: string };
}

type ApprovalStatus = 'APPROVED' | 'REVISION';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const managerActualText = (item: IApprovalKpiItem) =>
  item.manager_actual === null || item.manager_actual === undefined ? '' : String(item.manager_actual);

/** A manager_actual input counts as filled when it holds a number ≥ 0. */
const isFilledActual = (val: string | undefined) =>
  val !== '' && val !== undefined && !isNaN(Number(val)) && Number(val) >= 0;

/**
 * Loads a KPI for approval. With `prefillAob`, a SUBMITTED KPI is saved once with each item's
 * manager_actual (falling back to the employee's actual value) so the response carries the initial AOB.
 */
async function fetchApprovalDetail(tenantId: number, id: string, prefillAob: boolean): Promise<IApprovalKpiDetail> {
  const response = await getKpiApprovalDetail(tenantId, id);
  const raw: IApprovalKpiDetail = response?.data ?? response;
  if (!prefillAob || raw.status !== 'SUBMITTED' || !raw.items?.length) return raw;

  const itemsToUpdate = raw.items.map(item => ({
    source_item_id: item.id,
    manager_actual: Number(item.manager_actual ?? item.actual_value ?? 0),
  }));
  try {
    const updated = await updateKpiApprovalItems(tenantId, id, itemsToUpdate);
    return updated?.data ?? updated;
  } catch (error) {
    console.error('Failed to pre-fetch AOB', error);
    return raw;
  }
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function ApprovalKpiDetailSkeleton() {
  return (
    <div className="px-5 space-y-6">
      <div>
        <Skeleton className="h-5 w-24 mb-3" />
        <div className="space-y-3">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="flex items-center gap-2">
              <Skeleton className="h-4 w-40 flex-shrink-0" />
              <Skeleton className="h-4 w-48" />
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-xl border border-gray-200 overflow-hidden">
        <Skeleton className="h-10 w-full" />
        {[1, 2, 3].map(i => (
          <div key={i} className="flex gap-4 px-4 py-3 border-t border-gray-100">
            <Skeleton className="h-5 w-6 flex-shrink-0" />
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-20 flex-shrink-0" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Sections ─────────────────────────────────────────────────────────────────

function KpiDetailInfo({ data }: { data: IApprovalKpiDetail }) {
  const startDate = data.start_date ?? data.template?.start_date;
  const endDate = data.end_date ?? data.template?.end_date;
  const period = startDate && endDate ? `${formatDate(startDate)} - ${formatDate(endDate)}` : data.period ?? '-';
  const hasProgress = data.progress !== null && data.progress !== undefined;

  return (
    <div>
      <h2 className="text-base font-semibold text-gray-800 mb-3">Detail</h2>
      <div className="space-y-2">
        <InfoRow label="Nama Karyawan" value={data.user?.name ?? '-'} />
        {data.user?.email && <InfoRow label="Email" value={data.user.email} />}
        <InfoRow label="Nama Template" value={data.template?.name ?? '-'} />
        <InfoRow label="Periode" value={period} />
        <InfoRow label="Status" value={<StatusBadge status={data.status} />} />
        {hasProgress && <InfoRow label="Progress" value={`${data.progress}%`} />}
      </div>
    </div>
  );
}

interface ManagerNotesSectionProps {
  readOnly: boolean;
  savedNotes: string;
  notes: string;
  onNotesChange: (value: string) => void;
  isSubmitted: boolean;
  canApprove: boolean;
  isApproving: boolean;
  isRevising: boolean;
  onApproval: (status: ApprovalStatus) => void;
}

/** Manager notes with the approve/revise actions; read-only mode shows the saved notes as text. */
function ManagerNotesSection({
  readOnly,
  savedNotes,
  notes,
  onNotesChange,
  isSubmitted,
  canApprove,
  isApproving,
  isRevising,
  onApproval,
}: ManagerNotesSectionProps) {
  if (readOnly) {
    if (!savedNotes) return null;
    return (
      <div className="pb-6">
        <InfoRow label="Catatan Manajer" value={savedNotes} />
      </div>
    );
  }

  const isBusy = isApproving || isRevising;

  return (
    <div className="space-y-4 pb-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Catatan Manajer <span className="text-gray-400 font-normal">(opsional)</span>
        </label>
        <textarea
          rows={4}
          placeholder="Tambahkan catatan untuk karyawan..."
          value={notes}
          onChange={e => onNotesChange(e.target.value)}
          disabled={!isSubmitted}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
        />
      </div>

      {isSubmitted && (
        <div className="flex gap-2">
          <SecondaryButton type="button" onClick={() => onApproval('REVISION')} loading={isRevising} disabled={isBusy}>
            Kembalikan untuk Revisi
          </SecondaryButton>
          <span title={canApprove ? undefined : 'Isi semua Realisasi Manager sebelum menyetujui'}>
            <SubmitButton
              type="button"
              onClick={() => onApproval('APPROVED')}
              loading={isApproving}
              disabled={isBusy || !canApprove}
            >
              Setujui
            </SubmitButton>
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface ApprovalKpiDetailProps {
  readOnly?: boolean;
}

export default function ApprovalKpiDetail({ readOnly = false }: ApprovalKpiDetailProps) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentTenant } = useTenantState();

  const [data, setData] = useState<IApprovalKpiDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [nameMap, setNameMap] = useState<NameMap>({ keyActivities: {}, jobDescs: {} });
  const [notesManager, setNotesManager] = useState('');
  const [isApproving, setIsApproving] = useState(false);
  const [isRevising, setIsRevising] = useState(false);

  // manager_actual local state: itemId → string value
  const [managerActuals, setManagerActuals] = useState<Record<number, string>>({});

  // Debounce timers per item
  const debounceTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  const tenantSlug = currentTenant?.slug ?? currentTenant?.id;
  const status = data?.status?.toUpperCase() ?? '';
  const isSubmitted = status === 'SUBMITTED';
  const canEditActuals = !readOnly && (isSubmitted || status === 'REVISION');
  const items = data?.items ?? [];
  // All manager_actual inputs must be filled (non-empty, numeric, ≥ 0) to enable Setujui
  const allManagerActualsFilled = items.length > 0 && items.every(item => isFilledActual(managerActuals[item.id]));

  const view = readOnly
    ? { listPath: 'kpi-pengguna', title: 'Detail User KPI', backTitle: 'Kembali ke User KPI' }
    : { listPath: 'kpi-approval', title: 'Detail Approval KPI', backTitle: 'Kembali ke Approval KPI' };

  // ─── Fetch ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    const fetchDetail = async () => {
      if (!currentTenant?.id || !id) return;
      try {
        const raw = await fetchApprovalDetail(currentTenant.id, id, !readOnly);
        setData(raw);
        setNotesManager(raw.notes_manager ?? '');

        if (raw.items?.length) {
          setManagerActuals(Object.fromEntries(raw.items.map(item => [item.id, managerActualText(item)])));
          setNameMap(await resolveNameMap(currentTenant.id, raw.items));
        }
      } catch {
        // silent fail
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [currentTenant?.id, id, readOnly]);

  // ─── Debounced manager_actual update ────────────────────────────────────────

  const saveManagerActual = useCallback(
    async (itemId: number, numVal: number) => {
      if (!currentTenant?.id || !id) return;
      try {
        const updated = await updateKpiApprovalItems(currentTenant.id, id, [
          { source_item_id: itemId, manager_actual: numVal },
        ]);
        const raw: IApprovalKpiDetail = updated?.data ?? updated;
        setData(raw);
        if (!Array.isArray(raw.items)) return;
        // Keep the value being typed; take the server's value for every other item.
        setManagerActuals(prev => ({
          ...prev,
          ...Object.fromEntries(
            raw.items.filter(item => item.id !== itemId).map(item => [item.id, managerActualText(item)]),
          ),
        }));
      } catch (error) {
        errorHandler(error);
      }
    },
    [currentTenant?.id, id],
  );

  const handleManagerActualChange = useCallback(
    (itemId: number, value: string) => {
      setManagerActuals(prev => ({ ...prev, [itemId]: value }));
      clearTimeout(debounceTimers.current[itemId]);

      const numVal = parseFloat(value);
      if (value === '' || isNaN(numVal) || numVal < 0) return;

      debounceTimers.current[itemId] = setTimeout(() => saveManagerActual(itemId, numVal), 500);
    },
    [saveManagerActual],
  );

  // ─── Approval actions ────────────────────────────────────────────────────────

  const handleApproval = async (approvalStatus: ApprovalStatus) => {
    if (!currentTenant?.id || !id) return;
    const isApprove = approvalStatus === 'APPROVED';
    const setter = isApprove ? setIsApproving : setIsRevising;
    setter(true);
    try {
      const updated = await updateKpiApproval(currentTenant.id, id, {
        status: approvalStatus,
        notes_manager: notesManager,
      });
      setData(updated?.data ?? updated);
      toast.success(isApprove ? 'KPI berhasil disetujui' : 'KPI dikembalikan untuk revisi');
    } catch (error) {
      errorHandler(error);
    } finally {
      setter(false);
    }
  };

  const renderManagerActualInput = (item: IApprovedKpiItem) => (
    <input
      type="number"
      min={0}
      name="manager_actual"
      placeholder="-"
      value={managerActuals[item.id] ?? ''}
      onChange={e => handleManagerActualChange(item.id, e.target.value)}
      className="w-24 px-2 py-1 border border-gray-300 rounded-lg text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
      data-testid={`manager-actual-${item.id}`}
    />
  );

  // ─── Render ──────────────────────────────────────────────────────────────────

  const renderBody = (detail: IApprovalKpiDetail) => {
    if (status === 'APPROVED') {
      return (
        <ApprovedKpiView
          items={detail.items}
          nameMap={nameMap}
          totalScore={detail.score}
          category={detail.category}
          notesManager={detail.notes_manager}
        />
      );
    }
    return (
      <>
        {items.length > 0 && (
          <KpiItemsTable
            items={detail.items}
            nameMap={nameMap}
            totalScore={detail.score}
            category={detail.category}
            renderManagerActual={canEditActuals ? renderManagerActualInput : undefined}
          />
        )}
        <ManagerNotesSection
          readOnly={readOnly}
          savedNotes={detail.notes_manager}
          notes={notesManager}
          onNotesChange={setNotesManager}
          isSubmitted={isSubmitted}
          canApprove={allManagerActualsFilled}
          isApproving={isApproving}
          isRevising={isRevising}
          onApproval={handleApproval}
        />
      </>
    );
  };

  const renderContent = () => {
    if (loading) return <ApprovalKpiDetailSkeleton />;
    if (!data) {
      return (
        <div className="px-5">
          <p className="text-gray-400 text-sm">Data tidak tersedia</p>
        </div>
      );
    }
    return (
      <div className="px-5 space-y-6">
        {/* Status banner — shown when action is no longer available */}
        {!isSubmitted && <ApprovalStatusBanner status={data.status} />}
        <KpiDetailInfo data={data} />
        {renderBody(data)}
      </div>
    );
  };

  return (
    <section className="w-full">
      <CompanyHeader
        title={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(`/${tenantSlug}/${view.listPath}`)}
              className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
              title={view.backTitle}
            >
              <ArrowLeft size={20} />
            </button>
            <span>{loading ? view.title : `${view.title} - ${data?.template?.name ?? '-'}`}</span>
          </div>
        }
      />

      <div className="mx-5 border-b border-gray-200 mb-5" />

      {renderContent()}
    </section>
  );
}
